import axios from 'axios';
import { getItem, removeItem, setItem } from '../utils/localStorageControl';
import Cookies from 'js-cookie';
import { notification } from 'antd';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

const authHeader = () => ({
  Authorization: `Bearer ${getItem('access_token')}`,
});

const client = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    Authorization: `Bearer ${getItem('access_token')}`,
    'Content-Type': 'application/json',
  },
});

const allClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

class DataService {
  static get(path = '') {
    return client({
      method: 'GET',
      url: path,
      headers: { ...authHeader() },
    });
  }

  static getBlob(path = '') {
    return client({
      method: 'GET',
      url: path,
      responseType: 'blob',
      headers: { ...authHeader() },
    });
  }

  static getUrl(path = '') {
    return allClient({
      method: 'GET',
      url: path,
      headers: { ...authHeader() },
    });
  }

  static post(path = '', data = {}, optionalHeader = {}) {
    return client({
      method: 'POST',
      url: path,
      data,
      headers: { ...authHeader(), ...optionalHeader },
    });
  }

  static patch(path = '', data = {}) {
    return client({
      method: 'PATCH',
      url: path,
      data: JSON.stringify(data),
      headers: { ...authHeader() },
    });
  }

  static put(path = '', data = {}) {
    return client({
      method: 'PUT',
      url: path,
      data: JSON.stringify(data),
      headers: { ...authHeader() },
    });
  }

  static delete(path = '') {
    return client({
      method: 'DELETE',
      url: path,
      headers: { ...authHeader() },
    });
  }

  static upload(path = '', formData: FormData) {
    return client({
      method: "POST",
      url: path,
      data: formData,
      headers: {
        ...authHeader(),
        "Content-Type": "multipart/form-data",
      },
    });
  }

  static saveDraft(data: any, _id?: string, forceReplace?: boolean, name?: string, type: string = 'WAREHOUSE_EXIT') {
    const user: any = getItem('user') || {};
    return this.post('/inventories/draft', { data, type, userId: user.id || user._id, _id, forceReplace, name });
  }

  static getDraft(type: string = 'WAREHOUSE_EXIT') {
    const user: any = getItem('user') || {};
    return this.get(`/inventories/draft?userId=${user.id || user._id}&type=${type}`);
  }

  static deleteDraft(draftId: string) {
    console.log('Deleting draft...', draftId);
    return this.delete(`/inventories/draft/${draftId}`);
  }

  static getConfigPetitions(filter?: string) {
    return this.get(`/config-petitions${filter ? `?${filter}` : ''}`);
  }

  static getConfigPetitionById(id: string) {
    return this.get(`/config-petitions/${id}`);
  }

  static createConfigPetition(data: any) {
    return this.post('/config-petitions', data);
  }

  static approveConfigPetition(id: string) {
    return this.post(`/config-petitions/${id}/approve`);
  }

  static rejectConfigPetition(id: string, rejectReason: string) {
    return this.post(`/config-petitions/${id}/reject`, { rejectReason });
  }

  static cancelConfigPetition(id: string) {
    return this.post(`/config-petitions/${id}/cancel`);
  }

  static signConfigPetition(id: string, signature: any, assignedUserIdOrRecogeSignature?: string | any, exitEvidences?: string[]) {
    const payload: any = { signature, exitEvidences };
    if (typeof assignedUserIdOrRecogeSignature === 'string') {
      payload.assignedUserId = assignedUserIdOrRecogeSignature;
    } else {
      payload.recogeSignature = assignedUserIdOrRecogeSignature;
    }
    return this.post(`/config-petitions/${id}/sign`, payload);
  }

  static acceptConfigPickup(id: string) {
    return this.post(`/config-petitions/${id}/accept-pickup`);
  }

  static rejectConfigPickup(id: string, rejectReason: string) {
    return this.post(`/config-petitions/${id}/reject-pickup`, { rejectReason });
  }

  static getMyPickups() {
    const user: any = getItem('user') || {};
    const userId = user.id || user._id;
    return this.get(`/config-petitions?assignedPickupUser=${userId}&status=PENDING_PICKUP`);
  }

  static confirmArrivalConfig(id: string, entregaSignature: any, responsableConfigSignature: any, arrivalEvidences: string[]) {
    return this.post(`/config-petitions/${id}/confirm-arrival`, { entregaSignature, responsableConfigSignature, arrivalEvidences });
  }

  static rejectConfigMovement(id: string, rejectReason: string) {
    return this.post(`/config-petitions/${id}/reject-movement`, { rejectReason });
  }

  static getMyConfigProjects() {
    return this.get('/proyects/my-config-projects');
  }

  static getConfigProducts(proyectID: string, search?: string) {
    return this.get(`/inventories/get-products-config?proyectID=${proyectID}${search ? `&search=${search}` : ''}`);
  }

  /**
   * Productos en configuración (configStock > 0) por proyecto o sin-proyecto.
   * Usa el endpoint /inventories/config-products que reconoce 'sin-proyecto'
   * como valor del query param `proyect` y filtra estrictamente por configStock.
   */
  static getProductsInConfiguracion(proyect: string | null, search?: string) {
    const params = new URLSearchParams();
    if (proyect === null) {
      params.set('proyect', 'sin-proyecto');
    } else if (proyect) {
      params.set('proyect', proyect);
    }
    if (search && search.trim()) params.set('search', search.trim());
    const qs = params.toString();
    return this.get(`/inventories/config-products${qs ? `?${qs}` : ''}`);
  }

  static getBitacoras(filter?: string) {
    return this.get(`/bitacoras${filter ? `?${filter}` : ''}`);
  }

  static getBitacoraById(id: string) {
    return this.get(`/bitacoras/${id}`);
  }

  static createBitacora(data: any) {
    return this.post('/bitacoras', data);
  }

  static updateBitacora(id: string, data: any) {
    return this.patch(`/bitacoras/${id}`, data);
  }

  static publishBitacora(id: string) {
    return this.post(`/bitacoras/${id}/publish`);
  }

  static deleteBitacora(id: string) {
    return this.delete(`/bitacoras/${id}`);
  }

  // ── Bitácoras de Configuración ──────────────────────────────
  static getBitacorasConfig(filter?: string) {
    return this.get(`/bitacora-config${filter ? `?${filter}` : ''}`);
  }

  static getBitacoraConfigById(id: string) {
    return this.get(`/bitacora-config/${id}`);
  }

  static createBitacoraConfig(data: any) {
    return this.post('/bitacora-config', data);
  }

  static updateBitacoraConfig(id: string, data: any) {
    return this.patch(`/bitacora-config/${id}`, data);
  }

  static publishBitacoraConfig(id: string) {
    return this.post(`/bitacora-config/${id}/publish`);
  }

  static deleteBitacoraConfig(id: string) {
    return this.delete(`/bitacora-config/${id}`);
  }

  static getBitacoraConfigUploadUrl(bitacoraConfigId: string, entryId: number, data: { fileName: string; contentType: string }) {
    return this.post(`/bitacora-config/${bitacoraConfigId}/entries/${entryId}/upload-url`, data);
  }

  static resolveIncidencia(movementId: string, data: { products: any[]; comment?: string; evidences?: string[] }) {
    return this.post(`/inventories/${movementId}/resolve-incidencia`, data);
  }

  // ── Herramientas: catálogo + tipos + kits ──────────────────────
  static getWarehouses() {
    return this.get('/warehouses');
  }

  static getToolTypes(filter?: string) {
    return this.get(`/tool-types${filter ? `?${filter}` : ''}`);
  }

  static createToolType(data: { name: string; description?: string; isConsumable?: boolean; isSingleUse?: boolean }) {
    return this.post('/tool-types', data);
  }

  static updateToolType(id: string, data: { name?: string; description?: string; isConsumable?: boolean; isSingleUse?: boolean }) {
    return this.patch(`/tool-types/${id}`, data);
  }

  static deleteToolType(id: string) {
    return this.delete(`/tool-types/${id}`);
  }

  static getTools(filter?: string) {
    return this.get(`/tools${filter ? `?${filter}` : ''}`);
  }

  static getToolById(id: string) {
    return this.get(`/tools/${id}`);
  }

  static createTool(data: { name: string; brand?: string; model?: string; toolTypeID: string; unitPrice: number; description?: string }) {
    return this.post('/tools', data);
  }

  static updateTool(id: string, data: any) {
    return this.patch(`/tools/${id}`, data);
  }

  static deleteTool(id: string) {
    return this.delete(`/tools/${id}`);
  }

  static uploadToolImage(id: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    return this.upload(`/tools/${id}/image`, formData);
  }

  static getToolKits(filter?: string) {
    return this.get(`/tool-kits${filter ? `?${filter}` : ''}`);
  }

  static getToolKitById(id: string) {
    return this.get(`/tool-kits/${id}`);
  }

  static createToolKit(data: { name: string; description?: string; location?: string; items: { toolID: string; quantity: number }[] }) {
    return this.post('/tool-kits', data);
  }

  static updateToolKit(id: string, data: any) {
    return this.patch(`/tool-kits/${id}`, data);
  }

  static updateToolInventoryLocation(id: string, location: string) {
    return this.patch(`/tool-inventories/${id}/location`, { location });
  }

  static uploadToolKitImage(id: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    return this.upload(`/tool-kits/${id}/image`, formData);
  }

  static deleteToolKit(id: string) {
    return this.delete(`/tool-kits/${id}`);
  }

  // ── Carta Porte: consulta de viajes ───────────────────────────
  static getCartasPorte(filter?: string) {
    return this.get(`/carta-portes${filter ? `?${filter}` : ''}`);
  }

  static getToolInventories(filter?: string) {
    return this.get(`/tool-inventories${filter ? `?${filter}` : ''}`);
  }

  static getToolInventoryById(id: string) {
    return this.get(`/tool-inventories/${id}`);
  }

  static getToolInventoryLoanHolders(id: string) {
    return this.get(`/tool-inventories/${id}/loan-holders`);
  }

  static getToolInventoryMovements(id: string, filter?: string) {
    return this.get(`/tool-inventories/${id}/movements${filter ? `?${filter}` : ''}`);
  }

  static createToolInventoryEntry(formData: FormData) {
    return this.upload('/tool-inventories/entry', formData);
  }

  static getPendingToolPurchases() {
    return this.get('/tool-inventories/pending-purchases');
  }

  static signToolLoanCustody(loanId: string, image: string) {
    return this.post(`/tool-loans/${loanId}/sign-custody`, { image });
  }

  static getToolMovements(filter?: string) {
    return this.get(`/tool-movements${filter ? `?${filter}` : ''}`);
  }

  // ── Herramientas: préstamos ───────────────────────────────────
  static getToolLoans(filter?: string) {
    return this.get(`/tool-loans${filter ? `?${filter}` : ''}`);
  }

  /** Conteo de préstamos con herramientas perdidas (banner). */
  static getToolLoanLossSummary() {
    return this.get(`/tool-loans/loss-summary`);
  }

  static getToolLoanById(id: string) {
    return this.get(`/tool-loans/${id}`);
  }

  static createToolLoan(data: {
    warehouseID: string;
    items: { toolID: string; quantity: number; kitID?: string }[];
    responsableId: string;
    projectId?: string;
    pickupDate?: string;
    returnDate?: string;
    note?: string;
  }) {
    return this.post('/tool-loans', data);
  }

  static approveToolLoan(id: string, data: { items: { toolID: string; serialNumbers: string[] }[]; evidence: string[]; responsableSignature: { image: string; signedAt: string } }) {
    return this.post(`/tool-loans/${id}/approve`, data);
  }

  static rejectToolLoan(id: string, data: { reason?: string }) {
    return this.post(`/tool-loans/${id}/reject`, data);
  }

  static returnToolLoan(id: string, data: {
    items: { toolID: string; serialsReturned?: string[]; serialsLost?: string[]; returnedQuantity?: number; lostQuantity?: number }[];
    note?: string;
    evidence: string[];
    employeeId?: string;
    chargeLostPieces?: boolean;
    exemptionReason?: string;
    returnWarehouseID?: string;
  }) {
    return this.post(`/tool-loans/${id}/return`, data);
  }

  static uploadToolLoanEvidence(formData: FormData) {
    return this.upload('/tool-loans/upload-evidence', formData);
  }

  static requestToolLoanExtension(loanId: string, data: { newReturnDate: string; reason?: string; evidence: string[] }) {
    return this.post(`/tool-loans/${loanId}/extension`, data);
  }

  static reportToolLoanIncidencia(loanId: string, data: { toolID?: string; description: string; evidence: string[] }) {
    return this.post(`/tool-loans/${loanId}/incidencias`, data);
  }

  static approveToolLoanExtension(loanId: string, extensionId: string) {
    return this.post(`/tool-loans/${loanId}/extension/${extensionId}/approve`, {});
  }

  static rejectToolLoanExtension(loanId: string, extensionId: string, reason?: string) {
    return this.post(`/tool-loans/${loanId}/extension/${extensionId}/reject`, { reason });
  }

  // ── Herramientas: ajustes de inventario ───────────────────────
  static createToolAdjustment(data: { toolInventoryID: string; newStock: number; description: string; serialNumbers?: string[] }) {
    return this.post('/tool-adjustments', data);
  }

  static getToolAdjustments(filter?: string) {
    return this.get(`/tool-adjustments${filter ? `?${filter}` : ''}`);
  }

  static approveToolAdjustment(id: string) {
    return this.patch(`/tool-adjustments/${id}/approve`, {});
  }

  static rejectToolAdjustment(id: string) {
    return this.patch(`/tool-adjustments/${id}/reject`, {});
  }

  // ── Transportista: Entregas en Obra + Confirmar entrega en obra ──

  static getShippingCompanies() {
    return this.get('/shipping-companies');
  }

  static getTransportistas() {
    return this.get('/users/by-module?module=pwa_transportista&submodule=mis_entregas');
  }

  static getMyDeliveries(status?: string) {
    const qs = status ? `?status=${status}` : '';
    return this.get(`/inventories/my-deliveries${qs}`);
  }

  static getDeliveryById(id: string) {
    return this.get(`/inventories/movements/${id}`);
  }

  /**
   * Confirm delivery at the project site (transportista role).
   * Sends FormData with encargado signature + evidences + optional geo.
   */
  static confirmDelivery(
    movementId: string,
    payload: {
      encargadoSignature: string;
      encargadoName: string;
      encargadoSignedAt?: string;
      latitude?: number | null;
      longitude?: number | null;
      evidences?: string[];
    }
  ) {
    return this.post(`/inventories/${movementId}/confirm-delivery`, payload);
  }

  // ── Vehículo: Aceptar / Devolver vehículo asignado ──────────────

  static getMyVehicleLoan() {
    return this.get('/vehicle-loans/mine');
  }

  /** Datos de conductor del usuario logueado ({ esConductor, numeroConductor, nip }). */
  static getMyDriverData() {
    return this.get('/vehicle-loans/mine/conductor');
  }

  // §3.7 of the solicitud-de-vehiculo spec: auth()-only catalog of available
  // vehicles, scoped to what the request form's picker needs.
  static getVehiculosDisponibles() {
    return this.get('/vehicle-loans/vehiculos-disponibles');
  }

  // §3.1: the employee requests a vehicle. empleado comes from the token on
  // the backend, never from this payload. Las fotos van como string (sin
  // coordenadas) o {url, lat, lng} (geolocalizadas).
  static solicitarVehicleLoan(payload: {
    vehiculo: string;
    tipoTraslado: 'obra' | 'interno';
    objetivo: string;
    proyecto?: string | null;
    oficina?: 'MZT' | 'CDMX';
    // Empresa que representa el solicitante (sólo préstamos de obra).
    empresa?: 'TECNOCOM' | 'TECIMAC';
    fechaInicioUso: string | Date;
    fechaFinUso: string | Date;
    // La vigencia ya no la declara el usuario: la lee la IA. La licencia
    // sólo viaja cuando la IA no estaba disponible (folio sin verificar);
    // con licencia validada el backend la toma del perfil del empleado.
    licenciaVigencia?: string | Date;
    // Sólo lo fija el backend (lectura IA): licencia sin vencimiento (CDMX).
    licenciaPermanente?: boolean;
    licencia?: Array<string | { url: string; lat?: number; lng?: number }>;
  }) {
    return this.post('/vehicle-loans/solicitar', payload);
  }

  // Sube una imagen capturada (multipart) al bucket público y devuelve {url}.
  // La PWA la llama inmediatamente tras cada foto para no mantener base64 en
  // memoria (crasheos en dispositivos de gama baja). Content-Type explícito:
  // el default application/json de la instancia serializaría el FormData a
  // JSON y multer no vería el archivo.
  static uploadImagen(form: FormData) {
    return this.post('/uploads/imagen', form, { 'Content-Type': 'multipart/form-data' });
  }

  // §3.6: the employee cancels their own SOLICITADA request.
  static cancelarSolicitudVehicleLoan(loanId: string) {
    return this.patch(`/vehicle-loans/${loanId}/cancelar-solicitud`);
  }

  // ── Control de Activos (préstamos de radios / teléfonos) ──────────────

  // Catálogo completo de activos con su estado en vivo (la pantalla
  // re-fetchea al montar/enfocar: sin caché).
  static getActivosCatalogo() {
    return this.get('/active-loans/catalogo');
  }

  // Solicitudes/préstamos de activos del empleado autenticado.
  static getMisActiveLoans() {
    return this.get('/active-loans/mine');
  }

  // Solicitud de préstamo de un activo disponible (empleado del token).
  static solicitarActiveLoan(payload: {
    activo: string;
    proposito: string;
    fechaInicio: string | Date;
    fechaFin: string | Date;
  }) {
    return this.post('/active-loans/solicitar', payload);
  }

  // El empleado cancela su propia solicitud pendiente.
  static cancelarSolicitudActiveLoan(loanId: string) {
    return this.patch(`/active-loans/${loanId}/cancelar-solicitud`);
  }

  // El empleado devuelve un activo con préstamo VIGENTE (justificación
  // obligatoria): el préstamo pasa a FINALIZADO y el activo a disponible.
  static devolverActiveLoan(loanId: string, justificacion: string) {
    return this.post(`/active-loans/${loanId}/devolver`, { justificacion });
  }

  // Préstamos de un EMPLEADO (persona): el backend resuelve todos los
  // usuarios que comparten ese employeeId (2 cuentas pueden apuntar a la
  // misma persona) y devuelve los préstamos de cualquiera de ellas. Sin
  // filtro de estado (el backend solo acepta un valor exacto); el buscador
  // filtra VIGENTE/EXPIRADO en el cliente.
  static getActiveLoansByEmpleado(empleadoId: string) {
    return this.get(`/active-loans?empleadoId=${encodeURIComponent(empleadoId)}`);
  }

  static checkoutVehicleLoan(loanId: string, payload: Record<string, any>) {
    return this.post(`/vehicle-loans/${loanId}/checkout`, payload);
  }

  static checkinVehicleLoan(loanId: string, payload: Record<string, any>) {
    return this.post(`/vehicle-loans/${loanId}/checkin`, payload);
  }

  static getMyVehicleLoanHistory() {
    return this.get('/vehicle-loans/mine/historial');
  }

  static getMyVehicleLoanById(loanId: string) {
    return this.get(`/vehicle-loans/mine/${loanId}`);
  }

  static reportVehicleLoanIncidencia(loanId: string, payload: Record<string, any>) {
    return this.post(`/vehicle-loans/${loanId}/incidencias`, payload);
  }

  // Reporte diario de obra (upsert del día): entrada/salida del día con
  // combustible, observaciones y fotos geolocalizadas.
  static reportarDiaVehicleLoan(loanId: string, payload: Record<string, any>) {
    return this.post(`/vehicle-loans/${loanId}/reportes-diarios`, payload);
  }

  // fotosCierre: hasta 3 imágenes del cierre (string o {url, lat, lng}).
  static cerrarVehicleLoanIncidencia(
    loanId: string,
    incidenciaId: string,
    payload: { solucion: string; fotosCierre?: Array<string | { url: string; lat?: number; lng?: number }> }
  ) {
    return this.patch(`/vehicle-loans/${loanId}/incidencias/${incidenciaId}/cerrar`, payload);
  }

  static getProyectosActivos() {
    return this.get('/proyects/all/names');
  }
}

/**
 * axios interceptors runs before and after a request, letting the developer modify req,req more
 * For more details on axios interceptor see https://github.com/axios/axios#interceptors
 */
client.interceptors.request.use((config) => {
  // do something before executing the request
  // For example tag along the bearer access token to request header or set a cookie
  let requestConfig: any = config;
  const { headers } = config;
  requestConfig.headers = { ...headers, Authorization: `Bearer ${getItem('access_token')}` };
  return requestConfig;
});

// for multiple requests to be refreshed
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

client.interceptors.response.use(
  (response) => response,
  (error) => {
    /**
     * Do something in case the response returns an error code [3**, 4**, 5**] etc
     * For example, on token expiration retrieve a new access token, retry a failed request etc
     */
    const { response } = error;
    const originalRequest = error.config;

    if (response) {
      if (response.status === 500) {
        return Promise.reject(error); // Should verify if we want to reject or return response as before
      } else if (response.status === 401 || response.status === 403) {
        if (response.status === 401 && response.data?.message === 'Please authenticate' && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers['Authorization'] = 'Bearer ' + token;
                return client(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          const refreshToken = getItem('refresh_token');

          // Use axios directly to avoid circular dependency or interceptor loop
          return axios
            .post(`${API_ENDPOINT}/auth/refresh-tokens`, { refreshToken })
            .then((res) => {
              if (res.status === 200) {
                const { access, refresh } = res.data;
                setItem('access_token', access.token);
                setItem('refresh_token', refresh.token);

                // Update header for the original request
                originalRequest.headers['Authorization'] = 'Bearer ' + access.token;

                processQueue(null, access.token);
                return client(originalRequest);
              }
            })
            .catch((err) => {
              processQueue(err, null);
              Cookies.remove('logedIn');
              removeItem('access_token');
              removeItem('refresh_token');
              removeItem('user');
              notification['error']({
                message: 'Autenticación',
                description: 'Su sesión ha caducado por favor inicie sesión nuevamente',
              });
              window.location.replace(window.location.origin);
              return Promise.reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        }
        return Promise.reject(error);
      } else {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);
export { DataService };
